import { useEffect, useState } from 'react';
import recurringService from '@/services/recurringService';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Trash2, Pencil, Calendar, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const RecurringTransactions = () => {
    const [recurringRules, setRecurringRules] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    const [formData, setFormData] = useState({
        amount: '',
        type: 'expense',
        categoryId: '',
        description: '',
        frequency: 'monthly',
        startDate: new Date().toISOString().split('T')[0],
        isActive: true,
    });

    useEffect(() => {
        fetchData();
        fetchCategories();
    }, []);

    const fetchData = async () => {
        try {
            const data = await recurringService.getAll();
            setRecurringRules(data);
        } catch (error) {
            toast.error('Failed to load recurring transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this recurring rule?')) return;
        try {
            await recurringService.remove(id);
            toast.success('Recurring transaction deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const toggleActive = async (rule) => {
        try {
            await recurringService.update(rule.id, { ...rule, isActive: !rule.isActive });
            toast.success(`Recurring transaction ${rule.isActive ? 'paused' : 'resumed'}`);
            fetchData();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleEdit = (rule) => {
        setEditingRule(rule);
        setFormData({
            amount: rule.amount,
            type: rule.type,
            categoryId: rule.categoryId,
            description: rule.description || '',
            frequency: rule.frequency,
            startDate: rule.startDate,
            isActive: rule.isActive,
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingRule) {
                await recurringService.update(editingRule.id, formData);
                toast.success('Recurring transaction updated');
            } else {
                await recurringService.create(formData);
                toast.success('Recurring transaction created');
            }
            setShowDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const resetForm = () => {
        setFormData({
            amount: '',
            type: 'expense',
            categoryId: '',
            description: '',
            frequency: 'monthly',
            startDate: new Date().toISOString().split('T')[0],
            isActive: true,
        });
        setEditingRule(null);
    };

    const filteredCategories = categories.filter(cat => cat.type === formData.type);

    if (loading) {
        return <div className="p-8 text-center">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Recurring Transactions</h1>
                    <p className="text-muted-foreground mt-2">Manage your automated income and expenses</p>
                </div>
                <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Add Recurring
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Active Rules</CardTitle>
                    <CardDescription>Transactions generated automatically based on schedule</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Description</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Frequency</TableHead>
                                <TableHead>Next Run</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {recurringRules.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                        No recurring rules set up
                                    </TableCell>
                                </TableRow>
                            ) : (
                                recurringRules.map((rule) => (
                                    <TableRow key={rule.id}>
                                        <TableCell>
                                            <div className="font-medium">{rule.description || 'Untitled'}</div>
                                            <div className="text-xs text-muted-foreground">{rule.Category?.name}</div>
                                        </TableCell>
                                        <TableCell className={rule.type === 'income' ? 'text-emerald-600' : 'text-red-600'}>
                                            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(rule.amount)}
                                        </TableCell>
                                        <TableCell className="capitalize">{rule.frequency}</TableCell>
                                        <TableCell>{rule.nextRunDate}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={rule.isActive}
                                                    onCheckedChange={() => toggleActive(rule)}
                                                />
                                                <span className="text-sm text-muted-foreground">{rule.isActive ? 'Active' : 'Paused'}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => handleEdit(rule)}>
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.id)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingRule ? 'Edit Recurring Rule' : 'New Recurring Rule'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value, categoryId: '' })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="income">Income</SelectItem>
                                    <SelectItem value="expense">Expense</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={formData.categoryId} onValueChange={(value) => setFormData({ ...formData, categoryId: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {filteredCategories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Frequency</Label>
                                <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="daily">Daily</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                        <SelectItem value="yearly">Yearly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Start Date</Label>
                                <Input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="e.g., Rent, Netflix"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                            <Button type="submit">{editingRule ? 'Update' : 'Create'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RecurringTransactions;
